// Ciudades de la pestaña «Métricas»: la ciudad que escribe el candidato (texto libre) agrupada para contar.
//
// Distinta a propósito de plazas.ts (el filtro de Candidatos), que junta «CDMX y Edomex» en una sola plaza:
// aquí Estado de México / Toluca va APARTE de la Ciudad de México (Iliana, 16-oct: «se me mezclan»).
// Lo que no se dice queda en «sin_ciudad»; lo que no es ninguna, en «otra».
//
// ESPEJO: la sincronización de Carranza agrupa con las MISMAS listas en JavaScript
// (conversational-saas/docs/tenants/cyc/lab-rh-v2/portal/cyc-portal-sync.js, función ciudadMetrica).
// Si cambias una lista aquí, cámbiala allá: pruebas/unidad/ciudades-metricas.test.ts compara las dos.
//
// Cómo se decide (igual que plazas.ts): 1) por ESTADO («Qro», «N.L.», «Edomex», «CDMX»), gana el que aparece
// primero en el texto; 2) si no dice estado, por CIUDAD o municipio. Sin acentos, sin mayúsculas, palabra completa.
import { normalizarCiudad } from "./plazas";

export type ClaveCiudad = "monterrey" | "queretaro" | "cdmx" | "edomex" | "cancun" | "otra" | "sin_ciudad";

export type CiudadMetrica = { clave: ClaveCiudad; etiqueta: string; corta: string };

/** En el orden en que salen las tarjetas, las series y las columnas. */
export const CIUDADES: CiudadMetrica[] = [
  { clave: "monterrey", etiqueta: "Monterrey y área metropolitana", corta: "Monterrey" },
  { clave: "queretaro", etiqueta: "Querétaro (incluye San Juan del Río)", corta: "Querétaro" },
  { clave: "cdmx", etiqueta: "Ciudad de México", corta: "CDMX" },
  { clave: "edomex", etiqueta: "Estado de México / Toluca", corta: "Edomex / Toluca" },
  { clave: "cancun", etiqueta: "Cancún", corta: "Cancún" },
  { clave: "otra", etiqueta: "Otra ciudad", corta: "Otra" },
  { clave: "sin_ciudad", etiqueta: "No dijo su ciudad", corta: "Sin ciudad" },
];

export const CLAVES_CIUDAD: ClaveCiudad[] = CIUDADES.map((c) => c.clave);
export const esClaveCiudad = (v: unknown): v is ClaveCiudad => typeof v === "string" && (CLAVES_CIUDAD as string[]).includes(v);
export const ciudadPorClave = (c: ClaveCiudad): CiudadMetrica => CIUDADES.find((x) => x.clave === c) ?? CIUDADES[5];

type Grupo = { clave: Exclude<ClaveCiudad, "otra" | "sin_ciudad">; estados: string[]; ciudades: string[] };

/** Las listas. MISMO contenido que GRUPOS_CIUDAD en cyc-portal-sync.js. */
export const GRUPOS_CIUDAD: Grupo[] = [
  {
    clave: "monterrey",
    estados: ["nuevo leon", "n l", "nl"],
    ciudades: [
      "monterrey", "momterrey", "monterey", "mty", "area metropolitana de monterrey", "san pedro garza garcia", "san pedro",
      "san nicolas de los garza", "san nicolas", "guadalupe", "apodaca", "general escobedo", "escobedo", "santa catarina",
      "garcia", "cadereyta", "santiago", "salinas victoria", "pesqueria", "cienega de flores", "general zuazua", "zuazua",
      "el carmen", "montemorelos",
    ],
  },
  {
    clave: "queretaro",
    estados: ["queretaro", "qro"],
    ciudades: [
      "santiago de queretaro", "san juan del rio", "corregidora", "el marques", "pedro escobedo", "tequisquiapan", "juriquilla",
    ],
  },
  {
    clave: "cdmx",
    estados: ["ciudad de mexico", "cdmx", "cd de mexico", "cd mexico", "cd mx", "cd mex", "df", "d f", "distrito federal", "mexico df"],
    ciudades: [
      "coyoacan", "iztapalapa", "tlalpan", "miguel hidalgo", "cuauhtemoc", "azcapotzalco", "gustavo a madero", "alvaro obregon",
      "xochimilco", "santa fe", "tlahuac", "iztacalco", "venustiano carranza", "magdalena contreras", "cuajimalpa", "milpa alta",
      "polanco", "condesa", "roma norte",
    ],
  },
  {
    clave: "edomex",
    estados: ["estado de mexico", "edomex", "edo mex", "edo de mexico", "edo mexico", "estado mexico"],
    ciudades: [
      "toluca", "metepec", "zinacantepec", "lerma", "almoloya de juarez", "naucalpan", "tlalnepantla", "ecatepec", "nezahualcoyotl",
      "neza", "cuautitlan izcalli", "cuautitlan", "huixquilucan", "atizapan de zaragoza", "atizapan", "tecamac", "texcoco",
      "chalco", "valle de chalco", "ixtapaluca", "chimalhuacan", "coacalco", "tultitlan", "tultepec", "nicolas romero",
      "zumpango", "huehuetoca", "teoloyucan", "melchor ocampo", "los reyes la paz",
    ],
  },
  {
    clave: "cancun",
    estados: ["quintana roo", "q roo", "qroo"],
    ciudades: ["cancun", "puerto morelos", "playa del carmen"],
  },
];

function primera(texto: string, frases: string[]): number {
  const t = ` ${texto} `;
  let mejor = -1;
  for (const f of frases) {
    const i = t.indexOf(` ${f} `);
    if (i >= 0 && (mejor < 0 || i < mejor)) mejor = i;
  }
  return mejor;
}

function ganadora(texto: string, campo: "estados" | "ciudades"): ClaveCiudad | null {
  let mejor: { clave: ClaveCiudad; i: number } | null = null;
  for (const g of GRUPOS_CIUDAD) {
    const i = primera(texto, g[campo]);
    if (i >= 0 && (!mejor || i < mejor.i)) mejor = { clave: g.clave, i };
  }
  return mejor?.clave ?? null;
}

/** La ciudad (agrupada) de lo que escribió el candidato. Vacío → «sin_ciudad»; desconocido → «otra». */
export function ciudadMetricaDe(ciudad: string | null | undefined): ClaveCiudad {
  const t = normalizarCiudad(ciudad ?? "");
  if (!t) return "sin_ciudad";
  return ganadora(t, "estados") ?? ganadora(t, "ciudades") ?? "otra";
}
